import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { MqttSuspense } from "@/components/generic/mqtt-suspense";
import { MultiPropertyCardList } from "@/components/generic/multi-property-card-list";
import { MultiPropertyList } from "@/components/generic/multi-property-list";
import { Badge as BadgeUI } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { type ApiError, isApiError } from "@/data-interfaces/generics";
import type { LibraryTool, ToolPosition } from "@/data-interfaces/tools";
import { useMQTTAutoRefreshPublish, useMQTTSubscribe } from "@/hooks/use-mqtt";
import {
	getLibraryToolDetailsProperties,
	getLibraryToolPositions,
} from "@/lib/data-remappings/library-tool-details";
import { getToolImage } from "@/lib/image-utils";
import { getBgColor, LogLevel } from "@/lib/log-level";
import {
	getAppWrappedPageUrl,
	LIBRARY_TOOL_DETAILS_PAGE_URL,
	TOOLS_PAGE_URL,
} from "@/lib/page-routes";
import { TL_LIBRARY_TOOL_URL, TL_TOOL_POCKETS_URL } from "@/lib/request-paths";

export const Route = createFileRoute(
	getAppWrappedPageUrl(LIBRARY_TOOL_DETAILS_PAGE_URL),
)({
	component: LibraryToolDetails,
	validateSearch: (search: LibraryTool) => search,
});

function LibraryToolDetails() {
	const [t, _] = useTranslation();

	const navigate = useNavigate();

	const libraryToolSearchParam: LibraryTool = Route.useSearch();

	const [libraryTool, setLibraryTool] = useState<LibraryTool | ApiError>(
		libraryToolSearchParam,
	);
	const [toolPositions, setToolPositions] = useState<
		Array<ToolPosition> | ApiError | null
	>(null);

	useMQTTSubscribe(TL_LIBRARY_TOOL_URL, setLibraryTool);
	useMQTTSubscribe(TL_TOOL_POCKETS_URL, setToolPositions);

	useMQTTAutoRefreshPublish(30, TL_LIBRARY_TOOL_URL, {
		tool_class_id: libraryToolSearchParam.id,
	});
	useMQTTAutoRefreshPublish(30, TL_TOOL_POCKETS_URL, {
		tool_class_id: libraryToolSearchParam.id,
	});

	return (
		<div className="text-white p-8 flex flex-col flex-1">
			<h2 className="text-2xl sm:text-3xl hover:text-[#238b8c]">
				<button
					type="button"
					onClick={() => {
						navigate({
							to: TOOLS_PAGE_URL,
							search: () => ({ selectedTab: "library" }),
						});
					}}
				>
					{"< "}
					{t("Tools")}
				</button>
			</h2>

			<div className="grid grid-cols-1 2xl:grid-cols-2 grow">
				<div className="flex flex-1 flex-col grow items-center justify-center gap-y-4">
					{!isApiError(libraryTool) && (
						<>
							<img
								src={getToolImage(libraryTool.type.id)}
								className="w-75 h-75 2xl:w-125 2xl:h-125 rounded-full"
								alt={"Tool representation"}
							/>

							<div className="flex flex-row gap-x-2">
								{libraryTool.is_required && (
									<BadgeUI className={`${getBgColor(LogLevel.Warning)}`}>
										{t("Required")}
									</BadgeUI>
								)}
							</div>
						</>
					)}
				</div>
				<div className="flex flex-1 grow items-center justify-center">
					<Tabs defaultValue="description" className="flex flex-1 gap-y-8 mt-8">
						<div className="flex flex-1 flex-row justify-center">
							<TabsList>
								<TabsTrigger value="description" className="min-w-25">
									{t("Description")}
								</TabsTrigger>
								<TabsTrigger value="positions" className="min-w-25">
									{t("Positions")}
								</TabsTrigger>
							</TabsList>
						</div>

						<TabsContent value="description">
							<MqttSuspense
								objects={libraryTool}
								requestUrl={TL_LIBRARY_TOOL_URL}
								parameters={{ tool_class_id: libraryToolSearchParam.id }}
								genericError="Error retriving tool"
							>
								{(libraryTool) => (
									<MultiPropertyList
										multiPropertyObject={{
											properties: getLibraryToolDetailsProperties(libraryTool),
										}}
									/>
								)}
							</MqttSuspense>
						</TabsContent>

						<TabsContent value="positions" className="min-h-115">
							<MqttSuspense
								objects={toolPositions}
								requestUrl={TL_TOOL_POCKETS_URL}
								parameters={{ tool_class_id: libraryToolSearchParam.id }}
								genericError="Error retrieving tool pockets"
							>
								{(toolPositions) => (
									<MultiPropertyCardList
										multiPropertyObjects={getLibraryToolPositions(
											toolPositions,
										)}
										noItemsLabel="Tool not present in any position"
									/>
								)}
							</MqttSuspense>
						</TabsContent>
					</Tabs>
				</div>
			</div>
		</div>
	);
}
